import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Shield, Search, Camera, Upload, LogOut, AlertTriangle, CheckCircle, User, Mail, Phone, Calendar, Loader2, ZoomIn, ZoomOut, RotateCcw, Focus, ShieldAlert, Download, FileText, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { useSmartCapture } from "@/hooks/useSmartCapture";
import TheftHeatmap from "@/components/TheftHeatmap";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Video constraints with autofocus for better scanning
const VIDEO_CONSTRAINTS = {
  facingMode: "environment",
  width: { ideal: 1920 },
  height: { ideal: 1080 },
  focusMode: "continuous",
  advanced: [{ focusMode: "continuous" }]
};

function getToken() {
  return localStorage.getItem("police_session");
}

function authHeaders() {
  const tk = getToken();
  return tk ? { Authorization: "Bearer " + tk } : {};
}

// Haptic feedback helper - with iOS fallback using AudioContext
const triggerHaptic = (pattern = [50]) => {
  // Try standard Vibration API first (Android, some browsers)
  if ('vibrate' in navigator) {
    try {
      navigator.vibrate(pattern);
      return;
    } catch (e) {
      // Vibration failed, try fallback
    }
  }
  
  // iOS fallback: Create a subtle audio "tick" using AudioContext
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      const audioCtx = new AudioContextClass();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      oscillator.frequency.value = 150;
      oscillator.type = 'sine';
      
      const duration = Math.min(pattern[0] || 50, 30) / 1000;
      gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.1, audioCtx.currentTime + duration / 2);
      gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + duration);
      
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + duration);
      
      setTimeout(() => audioCtx.close(), 100);
    }
  } catch (e) {
    // Audio feedback not available, fail silently
  }
};

function SingleToolCard({ data, onImageClick, aiDetected }) {
  if (!data) return null;
  
  const stolen = data.is_stolen;
  const cardClass = stolen ? "bg-red-900/30 border-red-500" : "bg-green-900/20 border-green-700";
  const imageUrl = data.image_url ? (data.image_url.startsWith('/api') ? API_BASE + data.image_url : data.image_url) : null;
  
  return (
    <Card className={cardClass}>
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Tool Image */}
          {imageUrl && (
            <div 
              className="w-24 h-24 flex-shrink-0 rounded border border-border bg-black/20 overflow-hidden cursor-pointer hover:border-primary transition-colors"
              onClick={() => onImageClick(imageUrl)}
              title="Click to enlarge"
            >
              <img 
                src={imageUrl} 
                alt={`${data.manufacturer} ${data.tool_type}`}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              {stolen ? (
                <AlertTriangle className="w-5 h-5 text-red-400" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-400" />
              )}
              <Badge className={stolen ? "bg-red-600" : "bg-green-600"}>
                {stolen ? "STOLEN" : "NOT STOLEN"}
              </Badge>
              <span className="text-white font-bold">{data.manufacturer} {data.tool_type}</span>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-base md:text-sm">
              <div>
                <span className="text-blue-400">Serial: </span>
                <span className="text-white font-mono">{data.serial_number}</span>
              </div>
              <div>
                <span className="text-blue-400">Model: </span>
                <span className="text-white">{data.model_number || "N/A"}</span>
              </div>
              <div>
                <span className="text-blue-400">ID: </span>
                <span className="text-white font-mono text-xs">{data.tool_id}</span>
              </div>
              <div>
                <span className="text-blue-400">Status: </span>
                <span className="text-white">{data.status}</span>
              </div>
            </div>
          </div>
        </div>

        {/* AI-detected info for confirmation - shown for all found tools */}
        {aiDetected && (aiDetected.manufacturer || aiDetected.toolType || aiDetected.modelNumber) && (
          <div className={`mt-4 pt-4 border-t ${stolen ? 'border-red-700/50' : 'border-green-700/50'}`}>
            <p className={`text-sm ${stolen ? 'text-red-400' : 'text-green-400'} mb-2`}>AI Scan Detection</p>
            <div className="flex flex-wrap gap-3">
              {aiDetected.manufacturer && (
                <div className={`${stolen ? 'bg-red-950/50 border-red-800/50' : 'bg-green-950/50 border-green-800/50'} px-3 py-2 rounded border`}>
                  <p className={`text-sm ${stolen ? 'text-red-400' : 'text-green-400'}`}>Manufacturer</p>
                  <p className="text-sm text-white">{aiDetected.manufacturer}</p>
                </div>
              )}
              {aiDetected.toolType && (
                <div className={`${stolen ? 'bg-red-950/50 border-red-800/50' : 'bg-green-950/50 border-green-800/50'} px-3 py-2 rounded border`}>
                  <p className={`text-sm ${stolen ? 'text-red-400' : 'text-green-400'}`}>Tool Type</p>
                  <p className="text-sm text-white">{aiDetected.toolType}</p>
                </div>
              )}
              {aiDetected.modelNumber && (
                <div className={`${stolen ? 'bg-red-950/50 border-red-800/50' : 'bg-green-950/50 border-green-800/50'} px-3 py-2 rounded border`}>
                  <p className={`text-sm ${stolen ? 'text-red-400' : 'text-green-400'}`}>Model</p>
                  <p className="text-sm text-white">{aiDetected.modelNumber}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {stolen && data.owner && (
          <div className="mt-4 pt-4 border-t border-red-500/50">
            <h4 className="text-red-400 font-bold mb-2 flex items-center gap-2">
              <User className="w-4 h-4" />Owner Contact
            </h4>
            <div className="p-3 bg-red-900/30 rounded space-y-2">
              <p className="text-white text-base md:text-sm">
                <User className="w-4 h-4 inline text-red-400 mr-2" />
                {data.owner.name}
              </p>
              <p className="text-white text-base md:text-sm">
                <Mail className="w-4 h-4 inline text-red-400 mr-2" />
                <a href={"mailto:" + data.owner.email} className="hover:text-red-300">{data.owner.email}</a>
              </p>
              {data.owner.phone && (
                <p className="text-white text-base md:text-sm">
                  <Phone className="w-4 h-4 inline text-red-400 mr-2" />
                  <a href={"tel:" + data.owner.phone} className="hover:text-red-300">{data.owner.phone}</a>
                </p>
              )}
            </div>
            {data.stolen_date && (
              <div className="mt-3 p-2 bg-red-900/20 rounded flex items-center gap-2">
                <Calendar className="w-4 h-4 text-red-400" />
                <span className="text-red-300 text-base md:text-sm">
                  Marked stolen: {new Date(data.stolen_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ResultsDisplay({ data, onImageClick, aiDetected }) {
  if (!data) return null;
  
  const list = data.results || [];
  
  if (list.length === 0) {
    return (
      <Card className="bg-slate-800 border-blue-700">
        <CardContent className="p-6 text-center">
          <HelpCircle className="w-12 h-12 text-blue-400 mx-auto mb-3" />
          <h3 className="text-xl font-heading font-bold text-white mb-2">NOT FOUND</h3>
          <p className="text-blue-200 text-sm">
            No matching tools found in our database.
          </p>
          
          {/* AI-detected info for reference */}
          {aiDetected && (aiDetected.manufacturer || aiDetected.toolType || aiDetected.modelNumber) && (
            <div className="mt-4 pt-4 border-t border-blue-800/50">
              <p className="text-sm text-blue-400 mb-2">AI Scan Detection</p>
              <div className="flex flex-wrap justify-center gap-3">
                {aiDetected.manufacturer && (
                  <div className="bg-slate-900/50 px-3 py-2 rounded border border-blue-800/50">
                    <p className="text-sm text-blue-400">Manufacturer</p>
                    <p className="text-sm text-white">{aiDetected.manufacturer}</p>
                  </div>
                )}
                {aiDetected.toolType && (
                  <div className="bg-slate-900/50 px-3 py-2 rounded border border-blue-800/50">
                    <p className="text-sm text-blue-400">Tool Type</p>
                    <p className="text-sm text-white">{aiDetected.toolType}</p>
                  </div>
                )}
                {aiDetected.modelNumber && (
                  <div className="bg-slate-900/50 px-3 py-2 rounded border border-blue-800/50">
                    <p className="text-sm text-blue-400">Model</p>
                    <p className="text-sm text-white">{aiDetected.modelNumber}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }
  
  // Get the first stolen tool and its owner's other stolen tools
  const stolenTool = list.find(t => t.is_stolen);
  const otherStolenTools = stolenTool?.owner_other_stolen_tools || [];
  
  return (
    <div className="space-y-4">
      <h2 className="text-white font-heading text-xl">
        Results: {data.total || 0} found, {data.stolen_count || 0} stolen
      </h2>
      
      {/* Main results */}
      {list.slice(0, 5).map((tool, idx) => (
        <SingleToolCard key={tool.tool_id || idx} data={tool} onImageClick={onImageClick} aiDetected={aiDetected} />
      ))}
      
      {list.length > 5 && (
        <p className="text-blue-400 text-center">And {list.length - 5} more results...</p>
      )}
      
      {/* Other stolen tools from same owner */}
      {otherStolenTools.length > 0 && (
        <div className="mt-6 pt-6 border-t border-red-500/50">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <h3 className="text-red-400 font-heading text-lg uppercase">
              Other Stolen Tools - Same Owner ({otherStolenTools.length})
            </h3>
          </div>
          <p className="text-base md:text-sm text-red-300/80 mb-4">
            The following tools are also reported stolen by the same owner. Check if present:
          </p>
          <div className="space-y-3">
            {otherStolenTools.map((tool, idx) => {
              const toolImageUrl = tool.image_url ? (tool.image_url.startsWith('/api') ? API_BASE + tool.image_url : tool.image_url) : null;
              return (
                <Card key={tool.tool_id || idx} className="bg-red-950/50 border-red-800">
                  <CardContent className="py-3 px-4">
                    <div className="flex gap-3">
                      {/* Tool Photo */}
                      {toolImageUrl ? (
                        <div 
                          className="w-20 h-20 flex-shrink-0 rounded border border-red-700 bg-black/30 overflow-hidden cursor-pointer hover:border-red-500 transition-colors"
                          onClick={() => onImageClick(toolImageUrl)}
                          title="Click to enlarge"
                        >
                          <img 
                            src={toolImageUrl} 
                            alt={`${tool.manufacturer} ${tool.tool_type}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-20 h-20 flex-shrink-0 rounded border border-red-800 bg-red-950/30 flex items-center justify-center">
                          <Camera className="w-6 h-6 text-red-700" />
                        </div>
                      )}
                      
                      {/* Tool Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-white font-semibold truncate">{tool.manufacturer} {tool.tool_type}</p>
                            <p className="text-red-300 text-base md:text-sm font-mono">S/N: {tool.serial_number}</p>
                            {tool.model_number && (
                              <p className="text-red-300/70 text-sm md:text-xs">Model: {tool.model_number}</p>
                            )}
                          </div>
                          <Badge className="bg-red-600 text-white flex-shrink-0">STOLEN</Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Police Report Export Component
function PoliceReportExport() {
  const [exporting, setExporting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showSelector, setShowSelector] = useState(false);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 2; y--) {
    years.push(y);
  }

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(
        `${API}/police/report/export?month=${selectedMonth}&year=${selectedYear}`, 
        { headers: authHeaders() }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `police_activity_report_${months[selectedMonth - 1]}_${selectedYear}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success(`Activity report downloaded!`);
        setShowSelector(false);
      } else {
        throw new Error("Export failed");
      }
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="relative">
      <Button 
        size="sm" 
        variant="outline"
        onClick={() => setShowSelector(!showSelector)}
        className="border-blue-600 text-white hover:bg-blue-900/30"
      >
        <Download className="w-4 h-4 mr-2" />
        Export Report
      </Button>
      
      {showSelector && (
        <div className="absolute right-0 top-full mt-2 bg-slate-800 border border-blue-700 rounded-lg p-4 z-50 shadow-xl min-w-[220px]">
          <p className="text-sm text-blue-400 mb-2 font-semibold">Select Report Month:</p>
          <div className="flex gap-2 mb-3">
            <select 
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="flex-1 bg-slate-900 border border-blue-800 rounded px-2 py-1 text-sm text-white"
            >
              {months.map((m, idx) => (
                <option key={m} value={idx + 1}>{m}</option>
              ))}
            </select>
            <select 
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="bg-slate-900 border border-blue-800 rounded px-2 py-1 text-sm text-white"
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <p className="text-sm text-blue-300/70 mb-3">
            Includes all force activity and your details
          </p>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              onClick={handleExport}
              disabled={exporting}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm"
            >
              {exporting ? "Exporting..." : "Download CSV"}
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={() => setShowSelector(false)}
              className="text-gray-400 text-sm"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PolicePortal() {
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const resultRef = useRef(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [serialNumber, setSerialNumber] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [modelNumber, setModelNumber] = useState("");
  const [toolType, setToolType] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [viewerImage, setViewerImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [focusPoint, setFocusPoint] = useState(null);
  const [showResultOverlay, setShowResultOverlay] = useState(false);
  const [showSerialHelp, setShowSerialHelp] = useState(false);
  const [showDewaltWarning, setShowDewaltWarning] = useState(false);

  // Tap-to-focus handler
  const handleTapToFocus = async (e) => {
    if (!webcamRef.current?.video) return;
    
    const video = webcamRef.current.video;
    const stream = video.srcObject;
    if (!stream) return;
    
    const track = stream.getVideoTracks()[0];
    if (!track) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    setFocusPoint({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    triggerHaptic([20]);
    setTimeout(() => setFocusPoint(null), 1000);
    
    try {
      const capabilities = track.getCapabilities();
      if (capabilities.focusMode) {
        const constraints = { advanced: [{ focusMode: "manual" }] };
        if (capabilities.focusDistance) {
          const minFocus = capabilities.focusDistance.min || 0;
          const maxFocus = capabilities.focusDistance.max || 1;
          constraints.advanced[0].focusDistance = minFocus + (maxFocus - minFocus) * 0.2;
        }
        await track.applyConstraints(constraints);
        setTimeout(async () => {
          try {
            await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
          } catch (e) {}
        }, 2000);
      }
    } catch (error) {
      console.log("Focus control not available");
    }
  };

  useEffect(function onMount() {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      const resp = await fetch(API + "/police/me", { headers: authHeaders() });
      if (!resp.ok) throw new Error("Not authenticated");
      const acc = await resp.json();
      setAccount(acc);
      loadStats();
    } catch (err) {
      localStorage.removeItem("police_session");
      navigate("/police");
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    try {
      const resp = await fetch(API + "/police/stats", { headers: authHeaders() });
      if (resp.ok) {
        const s = await resp.json();
        setStats(s);
      }
    } catch (err) {
      // ignore
    }
  }

  function doLogout() {
    fetch(API + "/police/logout", { method: "POST", headers: authHeaders() });
    localStorage.removeItem("police_session");
    navigate("/police");
  }

  function openImageViewer(imageUrl) {
    setViewerImage(imageUrl);
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
    setShowImageViewer(true);
  }

  function handleZoomIn() {
    setZoomLevel(prev => Math.min(prev + 0.5, 5));
  }

  function handleZoomOut() {
    setZoomLevel(prev => {
      const newZoom = Math.max(prev - 0.5, 1);
      if (newZoom <= 1) setPanPosition({ x: 0, y: 0 });
      return newZoom;
    });
  }

  function handleResetZoom() {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  }

  function handleMouseDown(e) {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
    }
  }

  function handleMouseMove(e) {
    if (isDragging && zoomLevel > 1) {
      setPanPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  }

  function handleMouseUp() {
    setIsDragging(false);
  }

  function handleWheel(e) {
    e.preventDefault();
    if (e.deltaY < 0) {
      setZoomLevel(prev => Math.min(prev + 0.25, 5));
    } else {
      setZoomLevel(prev => {
        const newZoom = Math.max(prev - 0.25, 1);
        if (newZoom <= 1) setPanPosition({ x: 0, y: 0 });
        return newZoom;
      });
    }
  }

  async function handleSearch(e) {
    e.preventDefault();
    if (!serialNumber && !manufacturer) {
      toast.error("Enter serial number and/or manufacturer");
      return;
    }
    setSearching(true);
    setResults(null);
    setShowResultOverlay(false);
    try {
      const params = new URLSearchParams();
      if (serialNumber) params.append("serial_number", serialNumber);
      if (manufacturer) params.append("manufacturer", manufacturer);
      // Note: model_number is NOT sent - it's display only to avoid missing stolen tools
      const resp = await fetch(API + "/police/lookup?" + params.toString(), { headers: authHeaders() });
      const d = await resp.json();
      setResults(d);
      
      // Show overlay and haptic feedback based on results
      if (d.stolen_count > 0) {
        // Strong haptic alert for stolen tools
        triggerHaptic([200, 100, 200, 100, 200]);
        setShowResultOverlay(true);
      } else if (d.total > 0) {
        // Normal feedback for found but not stolen
        triggerHaptic([50, 50, 100]);
        toast.success("Tool found - Not stolen");
      } else {
        // Light feedback for not found
        triggerHaptic([30, 50, 30]);
        toast.info("No tools found");
      }
      
      // Scroll to results
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      toast.error("Search failed");
      triggerHaptic([100, 50, 100]);
    } finally {
      setSearching(false);
    }
  }

  async function doOCR(blob) {
    try {
      const fd = new FormData();
      fd.append("file", blob, "scan.jpg");
      const resp = await fetch(API + "/police/ocr-lookup", { method: "POST", headers: authHeaders(), body: fd });
      const d = await resp.json();
      
      // Populate form fields with OCR results
      if (d.ocr_result) {
        if (d.ocr_result.serial_number) {
          setSerialNumber(d.ocr_result.serial_number);
        }
        if (d.ocr_result.manufacturer) {
          setManufacturer(d.ocr_result.manufacturer);
        }
        if (d.ocr_result.model_number) {
          setModelNumber(d.ocr_result.model_number);
        }
        if (d.ocr_result.tool_type) {
          setToolType(d.ocr_result.tool_type);
        }
      }
      
      if (d.lookup_result) {
        setResults(d.lookup_result);
        
        // Show overlay and haptic feedback based on results
        if (d.lookup_result.stolen_count > 0) {
          // Strong haptic alert for stolen tools
          triggerHaptic([200, 100, 200, 100, 200]);
          setShowResultOverlay(true);
        } else if (d.lookup_result.total > 0) {
          // Normal feedback for found but not stolen
          triggerHaptic([50, 50, 100]);
          toast.success("Tool found - Not stolen");
        } else {
          // Light feedback for not found
          triggerHaptic([30, 50, 30]);
          if (d.ocr_result && (d.ocr_result.serial_number || d.ocr_result.manufacturer)) {
            toast.info("Fields detected - No matching tools found");
          } else {
            toast.info("No tools found");
          }
        }
        
        // DeWalt serial number warning
        if (d.ocr_result && d.ocr_result.manufacturer && d.ocr_result.manufacturer.toLowerCase().includes("dewalt") && !d.ocr_result.serial_number) {
          setShowDewaltWarning(true);
        }
        
        // Scroll to results
        setTimeout(() => {
          resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else {
        toast.info("No text detected");
        triggerHaptic([30]);
      }
    } catch (err) {
      toast.error("OCR failed");
      triggerHaptic([100, 50, 100]);
    } finally {
      setScanning(false);
    }
  }

  // Smart capture callback
  const handleSmartCapture = useCallback(async (blob) => {
    setShowCamera(false);
    setScanning(true);
    await doOCR(blob);
  }, []);

  // Smart capture hook
  const smartCapture = useSmartCapture({
    webcamRef,
    active: showCamera,
    onCapture: handleSmartCapture,
  });

  async function captureImage() {
    smartCapture.manualCapture();
  }

  async function handleFileUpload(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setScanning(true);
    await doOCR(f);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <header className="border-b border-blue-900 bg-slate-800 sticky top-0 z-40 px-4 py-3 flex justify-between items-center">
        <div className="flex items-center gap-3">
          {account?.badge_image_url ? (
            <img src={account.badge_image_url} alt="Force Badge" className="w-10 h-10 md:w-12 md:h-12 rounded-full object-contain bg-white/10 p-0.5" />
          ) : (
            <Shield className="w-8 h-8 text-blue-400" />
          )}
          <div>
            <span className="font-heading text-lg md:text-xl font-bold text-white">{account ? account.authority_name : "Police Portal"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PoliceReportExport />
          <Button variant="ghost" onClick={doLogout} className="text-white" data-testid="logout-btn">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-4xl mx-auto">
        {/* Mobile Stats - Horizontal Scroll */}
        <div className={`md:hidden flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide mb-4 ${showCamera ? 'hidden' : ''}`}>
          <Card className="bg-slate-800 border-blue-900 flex-shrink-0 w-36">
            <CardContent className="p-3 text-center">
              <AlertTriangle className="w-5 h-5 text-blue-400 mx-auto mb-1 opacity-70" />
              <p className="text-2xl font-bold text-white">{stats ? stats.total_stolen_tools : 0}</p>
              <p className="text-sm text-blue-400 uppercase">Stolen in DB</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-blue-900 flex-shrink-0 w-36">
            <CardContent className="p-3 text-center">
              <Search className="w-5 h-5 text-blue-400 mx-auto mb-1 opacity-70" />
              <p className="text-2xl font-bold text-white">{stats ? stats.your_lookup_count : 0}</p>
              <p className="text-sm text-blue-400 uppercase">Your Lookups</p>
            </CardContent>
          </Card>
          <Card className="bg-red-950/50 border-red-800 flex-shrink-0 w-40">
            <CardContent className="p-3 text-center">
              <ShieldAlert className="w-5 h-5 text-red-400 mx-auto mb-1 opacity-70" />
              <p className="text-2xl font-bold text-red-300">{stats ? stats.force_stolen_found : 0}</p>
              <p className="text-sm text-red-400 uppercase">Force Found</p>
            </CardContent>
          </Card>
          <Card className="bg-red-950/50 border-red-800 flex-shrink-0 w-40">
            <CardContent className="p-3 text-center">
              <span className="text-lg text-red-400 mx-auto mb-1 opacity-70">£</span>
              <p className="text-xl font-bold text-red-300">{stats ? (stats.force_stolen_value || 0).toLocaleString() : 0}</p>
              <p className="text-sm text-red-400 uppercase">Force Value</p>
            </CardContent>
          </Card>
        </div>

        {/* Desktop Stats Grid */}
        <div className="hidden md:grid grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-800 border-blue-900">
            <CardContent className="p-4">
              <p className="text-blue-400 text-base">Stolen in DB</p>
              <p className="text-3xl font-bold text-white">{stats ? stats.total_stolen_tools : 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-blue-900">
            <CardContent className="p-4">
              <p className="text-blue-400 text-base">Your Lookups</p>
              <p className="text-3xl font-bold text-white">{stats ? stats.your_lookup_count : 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-red-950/50 border-red-800">
            <CardContent className="p-4">
              <p className="text-red-400 text-base">Force Stolen Found</p>
              <p className="text-3xl font-bold text-red-300">{stats ? stats.force_stolen_found : 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-red-950/50 border-red-800">
            <CardContent className="p-4">
              <p className="text-red-400 text-base">Force Stolen Value</p>
              <p className="text-2xl font-bold text-red-300">
                £{stats ? (stats.force_stolen_value || 0).toLocaleString() : 0}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Combined Scan & Search Card */}
        <Card className="bg-slate-800 border-blue-900 mb-6">
          <CardContent className="p-4 md:p-6">
            {showCamera ? (
              <div className="space-y-4">
                {/* Scan guidance - above viewfinder */}
                <div className="text-center py-2">
                  <p className="text-blue-200 text-base font-semibold">Hold your device approx 20cm away from the label. No need to fill the viewfinder.</p>
                </div>
                {/* Camera View Container - constrained height on mobile */}
                <div 
                  className="relative w-full overflow-hidden rounded-xl md:rounded border border-blue-800 cursor-pointer" 
                  style={{ height: '35vh', maxHeight: '35vh' }}
                  onClick={handleTapToFocus}
                >
                  <Webcam 
                    ref={webcamRef} 
                    audio={false} 
                    screenshotFormat="image/jpeg"
                    screenshotQuality={0.92}
                    videoConstraints={VIDEO_CONSTRAINTS} 
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ transform: `scale(${smartCapture.zoom})`, transformOrigin: 'center center' }}
                  />
                  {/* Camera overlay with quality-reactive frame */}
                  <div className="camera-overlay">
                    <div className="camera-frame" data-quality={smartCapture.quality}>
                      <div className="camera-corner camera-corner-tl" />
                      <div className="camera-corner camera-corner-tr" />
                      <div className="camera-corner camera-corner-bl" />
                      <div className="camera-corner camera-corner-br" />
                      {smartCapture.capturing && (
                        <div className="absolute left-0 right-0 h-0.5 bg-blue-400 scan-line" />
                      )}
                    </div>
                  </div>
                  {/* Zoom indicator */}
                  {smartCapture.zoom > 1 && (
                    <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full pointer-events-none">
                      {smartCapture.zoom.toFixed(1)}x
                    </div>
                  )}
                  {/* Tap to focus indicator */}
                  {focusPoint && (
                    <div 
                      className="absolute w-16 h-16 border-2 border-yellow-400 rounded-lg pointer-events-none animate-pulse"
                      style={{ 
                        left: focusPoint.x - 32, 
                        top: focusPoint.y - 32,
                        boxShadow: '0 0 20px rgba(250, 204, 21, 0.5)'
                      }}
                    >
                      <Focus className="w-6 h-6 text-yellow-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                    </div>
                  )}
                  {/* Quality hint */}
                  <div className={`absolute bottom-2 left-1/2 -translate-x-1/2 text-white text-xs px-3 py-1 rounded-full pointer-events-none transition-colors duration-300 ${
                    smartCapture.quality === 'green' ? 'bg-green-600/80' : 
                    smartCapture.quality === 'yellow' ? 'bg-yellow-600/80' : 'bg-red-600/80'
                  }`}>
                    {smartCapture.capturing ? 'Capturing...' :
                     smartCapture.hint === 'ready' ? 'Ready - press capture' :
                     smartCapture.hint === 'move-back' ? 'Move back slightly' :
                     smartCapture.hint === 'checking' ? 'Checking label...' :
                     'Align label - adjust lighting'}
                  </div>
                </div>
                
                {/* Buttons - always visible below camera */}
                <div className="flex gap-2">
                  <Button onClick={captureImage} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-14 md:h-10" disabled={scanning || smartCapture.capturing}>
                    {smartCapture.capturing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Camera className="w-4 h-4 mr-2" />}
                    Capture Now
                  </Button>
                  <Button variant="outline" onClick={function() { setShowCamera(false); }} className="border-blue-700 text-white h-14 md:h-10">
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Processing Overlay - Shows when scanning after camera closes */}
                {scanning ? (
                  <div className="bg-blue-900/50 border-2 border-blue-500/50 rounded-xl p-8 text-center">
                    <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
                    <p className="text-lg font-bold text-white">Scanning Tool...</p>
                    <p className="text-sm text-blue-100 mt-2">AI is analysing the label and searching the database</p>
                    <p className="text-sm text-yellow-400 mt-3">This can take up to 12 seconds</p>
                  </div>
                ) : (
                  <>
                    {/* Scan Button - Prominent */}
                    <Button 
                      className="w-full h-16 bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2 rounded-xl text-base font-bold"
                      onClick={function() { setShowCamera(true); }}
                    >
                      <Camera className="w-6 h-6" />
                      <span>Scan Tool Label</span>
                    </Button>
                    
                    {/* Upload - Secondary */}
                    <Button 
                      variant="ghost" 
                      className="w-full h-8 text-white hover:text-white/80 text-sm"
                      onClick={function() { fileInputRef.current && fileInputRef.current.click(); }}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Or upload an image
                    </Button>
                  </>
                )}
                
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                
                {/* Divider */}
                <div className="flex items-center gap-3 py-2">
                  <div className="flex-1 h-px bg-blue-800"></div>
                  <span className="text-sm text-blue-400 uppercase">or search manually</span>
                  <div className="flex-1 h-px bg-blue-800"></div>
                </div>
                
                {/* Manual Search Form */}
                <form onSubmit={handleSearch} className="space-y-3">
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <Label className="text-blue-200 text-sm">Serial Number</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowSerialHelp(true)}
                          className="text-blue-400 hover:text-blue-300 text-sm px-2 py-0 h-auto"
                        >
                          <HelpCircle className="w-3 h-3 mr-1" />
                          Help finding the Serial Number
                        </Button>
                      </div>
                      <Input 
                        value={serialNumber} 
                        onChange={function(e) { setSerialNumber(e.target.value); }} 
                        className="bg-slate-900 border-blue-800 text-white h-12 md:h-10" 
                        placeholder="e.g., DW123456789"
                        data-testid="serial-input" 
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-blue-200 text-sm">Manufacturer</Label>
                      <Input 
                        value={manufacturer} 
                        onChange={function(e) { setManufacturer(e.target.value); }} 
                        className="bg-slate-900 border-blue-800 text-white h-12 md:h-10" 
                        placeholder="e.g., DeWalt"
                        data-testid="manufacturer-input" 
                      />
                    </div>
                    
                    {/* AI-detected fields (read-only, not used for search) */}
                    {(modelNumber || toolType) && (
                      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-blue-800/50">
                        <div className="space-y-1">
                          <Label className="text-blue-300/70 text-sm">Model (AI detected)</Label>
                          <Input 
                            value={modelNumber} 
                            readOnly
                            className="bg-slate-950 border-blue-900/50 text-blue-200/70 h-10 md:h-9 text-sm cursor-not-allowed" 
                            placeholder="—"
                            data-testid="model-input" 
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-blue-300/70 text-sm">Tool Type (AI detected)</Label>
                          <Input 
                            value={toolType} 
                            readOnly
                            className="bg-slate-950 border-blue-900/50 text-blue-200/70 h-10 md:h-9 text-sm cursor-not-allowed" 
                            placeholder="—"
                            data-testid="tool-type-input" 
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 md:h-10 font-bold" disabled={searching} data-testid="search-btn">
                    {searching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                    Search
                  </Button>
                </form>
              </div>
            )}
          </CardContent>
        </Card>

        <div ref={resultRef}>
          <ResultsDisplay 
            data={results} 
            onImageClick={openImageViewer} 
            aiDetected={{
              manufacturer: manufacturer,
              toolType: toolType,
              modelNumber: modelNumber
            }}
          />
        </div>

        {/* Stolen Tool Alert Overlay - Mobile */}
        {showResultOverlay && results?.stolen_count > 0 && (
          <div className="fixed inset-0 z-50 md:hidden">
            {/* Backdrop with strong red tint */}
            <div 
              className="absolute inset-0 bg-red-950/95 backdrop-blur-sm"
              onClick={() => setShowResultOverlay(false)}
            />
            
            {/* Content */}
            <div className="relative h-full flex flex-col items-center justify-center p-6 overflow-y-auto">
              {/* Alert Header */}
              <div className="text-center mb-6 animate-pulse">
                <ShieldAlert className="w-20 h-20 text-red-500 mx-auto mb-4" />
                <h2 className="text-3xl font-heading text-red-400 uppercase tracking-wider">
                  STOLEN TOOL FOUND
                </h2>
                <p className="text-red-300 mt-2">
                  {results.stolen_count} stolen tool{results.stolen_count > 1 ? 's' : ''} detected
                </p>
              </div>
              
              {/* Scanned Tool Info */}
              {results.results?.filter(t => t.is_stolen).slice(0, 1).map(tool => (
                <Card key={tool.tool_id} className="w-full max-w-sm bg-red-950 border-red-600 mb-4">
                  <CardContent className="p-4">
                    <div className="text-center mb-3">
                      <Badge className="bg-red-600 text-white text-lg px-4 py-1">STOLEN</Badge>
                    </div>
                    <h3 className="text-white text-xl font-bold text-center mb-2">
                      {tool.manufacturer} {tool.tool_type}
                    </h3>
                    <p className="text-red-200 text-center mb-4">S/N: {tool.serial_number}</p>
                    
                    {tool.owner && (
                      <div className="bg-black/30 rounded-lg p-3 space-y-2">
                        <p className="text-red-300 text-sm font-semibold">Owner Contact:</p>
                        <div className="flex items-center gap-2 text-white">
                          <User className="w-4 h-4 text-red-400" />
                          <span>{tool.owner.name}</span>
                        </div>
                        {tool.owner.phone && (
                          <div className="flex items-center gap-2 text-white">
                            <Phone className="w-4 h-4 text-red-400" />
                            <a href={`tel:${tool.owner.phone}`} className="underline">{tool.owner.phone}</a>
                          </div>
                        )}
                        {tool.owner.email && (
                          <div className="flex items-center gap-2 text-white">
                            <Mail className="w-4 h-4 text-red-400" />
                            <a href={`mailto:${tool.owner.email}`} className="underline text-sm">{tool.owner.email}</a>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Other stolen tools warning */}
                    {tool.owner_other_stolen_tools?.length > 0 && (
                      <div className="mt-3 p-2 bg-red-900/50 rounded border border-red-700">
                        <p className="text-red-300 text-sm font-semibold flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          {tool.owner_other_stolen_tools.length} more stolen tool{tool.owner_other_stolen_tools.length > 1 ? 's' : ''} from this owner
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              
              {/* Dismiss Button */}
              <Button 
                onClick={() => setShowResultOverlay(false)}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 text-lg"
              >
                View Full Details
              </Button>
              
              <p className="text-red-400/70 text-sm mt-4">Tap anywhere to dismiss</p>
            </div>
          </div>
        )}

        {/* Image Viewer Modal */}
        <Dialog open={showImageViewer} onOpenChange={setShowImageViewer}>
          <DialogContent className="max-w-4xl w-[95vw] h-[85vh] p-0 bg-black/95 border-blue-900 overflow-hidden">
            <div className="absolute top-3 left-3 z-10 flex gap-2">
              <Button size="sm" variant="secondary" onClick={handleZoomIn} title="Zoom In">
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="secondary" onClick={handleZoomOut} title="Zoom Out">
                <ZoomOut className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="secondary" onClick={handleResetZoom} title="Reset">
                <RotateCcw className="w-4 h-4" />
              </Button>
              <span className="flex items-center px-2 text-sm text-white bg-black/50 rounded">
                {Math.round(zoomLevel * 100)}%
              </span>
            </div>
            <div 
              className="w-full h-full flex items-center justify-center overflow-hidden cursor-grab active:cursor-grabbing"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleWheel}
            >
              {viewerImage && (
                <img 
                  src={viewerImage} 
                  alt="Tool"
                  className="select-none object-contain"
                  style={{
                    maxWidth: zoomLevel === 1 ? '100%' : 'none',
                    maxHeight: zoomLevel === 1 ? '100%' : 'none',
                    width: zoomLevel > 1 ? 'auto' : undefined,
                    height: zoomLevel > 1 ? 'auto' : undefined,
                    transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                    transition: isDragging ? 'none' : 'transform 0.2s ease-out'
                  }}
                  draggable={false}
                />
              )}
            </div>
            <div className="absolute bottom-3 left-3 right-3 text-center">
              <p className="text-sm text-white/70">
                Scroll to zoom • Drag to pan when zoomed • Click outside to close
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Theft Heatmap */}
        <div className="mt-8">
          <TheftHeatmap isPolice={true} customAuthHeaders={authHeaders()} />
        </div>

        {/* DeWalt Serial Number Warning Dialog */}
        <Dialog open={showDewaltWarning} onOpenChange={setShowDewaltWarning}>
          <DialogContent className="max-w-sm p-0 overflow-hidden rounded-xl bg-slate-800 border-blue-800 flex flex-col" style={{ maxHeight: 'min(75vh, 75dvh)' }} data-testid="dewalt-warning-dialog">
            <div className="bg-[#febd17] px-4 py-4 flex items-center gap-3 flex-shrink-0">
              <img
                src="https://res.cloudinary.com/dzx4iay2g/image/upload/v1774575010/toolio/dewalt/iclbx1dmya0ugh3tdjka.jpg"
                alt="DeWalt logo"
                className="h-12 w-auto"
              />
              <div>
                <DialogTitle className="text-black font-heading uppercase text-base">DeWalt Serial Number</DialogTitle>
                <p className="text-black/70 text-sm">Brand-specific scanning issue</p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <p className="text-sm text-gray-300 leading-relaxed">
                Some DeWalt tools <strong className="text-white">do not include the needed serial number on the main rating label</strong>. Instead the number is engraved either:
              </p>
              <ul className="text-sm text-gray-300 leading-relaxed list-disc pl-5 space-y-1">
                <li>Inside the battery compartment</li>
                <li>Near the motor housing</li>
                <li>On the tool base on certain corded or stationary models</li>
              </ul>
              <p className="text-sm text-gray-300 leading-relaxed">
                Do not confuse the Serial Number with the Date Code, it is the <strong className="text-white">engraved Serial Number</strong> that uniquely identifies your tool.
              </p>
              <div className="rounded-lg overflow-hidden border border-blue-800">
                <img
                  src="https://res.cloudinary.com/dzx4iay2g/image/upload/v1774443669/toolio/dewalt/jyadkfqeawqy0lm2whlz.jpg"
                  alt="DeWalt serial number location under battery compartment"
                  className="w-full h-52 object-contain bg-black/20"
                />
                <p className="text-sm text-gray-400 p-2 text-center italic">Date code and serial number inside the battery compartment</p>
              </div>
              <div className="bg-[#febd17]/10 border border-[#febd17]/30 rounded-lg p-3">
                <p className="text-sm text-gray-200 leading-relaxed">
                  <strong className="text-white">Please enter the serial number manually</strong> in the search fields. Check under the battery or on the base of the tool.
                </p>
              </div>
            </div>
            <div className="px-4 pb-4 pt-2 border-t border-blue-800 flex-shrink-0">
              <Button
                className="w-full h-10 font-bold text-sm"
                style={{ backgroundColor: '#febd17', color: '#000' }}
                onClick={() => setShowDewaltWarning(false)}
                data-testid="dewalt-warning-ok-btn"
              >
                Got it, I'll enter it manually
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Serial Number Help Dialog */}
        <Dialog open={showSerialHelp} onOpenChange={setShowSerialHelp}>
          <DialogContent className="max-w-lg max-h-[85vh] p-0 bg-slate-800 border-blue-800">
            <DialogHeader className="p-6 pb-2">
              <DialogTitle className="font-heading uppercase text-lg text-white">Where to Find the Serial Number</DialogTitle>
              <p className="text-sm text-blue-200">
                Serial numbers are usually found on a label or engraved plate on the tool. Check inside battery compartments too - many manufacturers place labels or engraved serial numbers there.
              </p>
              <p className="text-sm text-blue-200 mt-2">
                <strong className="text-yellow-400">Tip:</strong> If the serial is engraved and hard to read, try rubbing a little chalk, Tipp-Ex or a finger dab of paint over it, then wipe off the excess - this makes the numbers stand out.
              </p>
            </DialogHeader>
            <ScrollArea className="h-[60vh] px-6 pb-6">
              <div className="space-y-6">
                
                {/* Bosch */}
                <div className="border-b border-blue-800 pb-4">
                  <h3 className="font-heading text-base font-bold text-blue-400 mb-2">Bosch</h3>
                  <p className="text-sm text-blue-100 mb-2">
                    Look for a silver or white label on the main body of the tool, usually near the motor housing or on the bottom. The serial number is typically a 10-digit number starting with "0" or "3".
                  </p>
                  <div className="text-sm text-blue-300 italic">Common locations: Bottom of tool, near the air vents, or on the battery compartment.</div>
                </div>

                {/* DeWalt */}
                <div className="border-b border-blue-800 pb-4">
                  <h3 className="font-heading text-base font-bold text-blue-400 mb-2">DeWalt</h3>
                  <p className="text-sm text-blue-100 mb-2">
                    DeWalt serial numbers are found on a yellow and black label, typically on the motor housing or handle. The format is usually 8-10 alphanumeric characters.
                  </p>
                  <div className="text-sm text-blue-300 italic">Common locations: Side of the motor housing, bottom of the handle, or near / under the battery slot.</div>
                </div>

                {/* Festool */}
                <div className="border-b border-blue-800 pb-4">
                  <h3 className="font-heading text-base font-bold text-blue-400 mb-2">Festool</h3>
                  <p className="text-sm text-blue-100 mb-2">
                    Festool tools have a data plate with serial number, usually on the underside or rear of the tool. Look for "S/N" or "Serial No." followed by numbers.
                  </p>
                  <div className="text-sm text-blue-300 italic">Common locations: Underside of tool, rear housing, or inside the battery compartment.</div>
                </div>

                {/* Hikoki (formerly Hitachi) */}
                <div className="border-b border-blue-800 pb-4">
                  <h3 className="font-heading text-base font-bold text-blue-400 mb-2">Hikoki / Hitachi</h3>
                  <p className="text-sm text-blue-100 mb-2">
                    Serial numbers are on a silver label on the main body. Older Hitachi tools may have the number engraved. Format is typically alphanumeric, 8-12 characters.
                  </p>
                  <div className="text-sm text-blue-300 italic">Common locations: Side of motor housing, near the cord entry point, or on the base plate.</div>
                </div>

                {/* Makita */}
                <div className="border-b border-blue-800 pb-4">
                  <h3 className="font-heading text-base font-bold text-blue-400 mb-2">Makita</h3>
                  <p className="text-sm text-blue-100 mb-2">
                    Makita serial numbers are on a white or silver label, usually on the side of the tool body near the motor. The serial is typically 6-8 digits, sometimes with a letter prefix.
                  </p>
                  <div className="text-sm text-blue-300 italic">Common locations: Side of motor housing, under the battery, or near the air intake.</div>
                </div>

                {/* Milwaukee */}
                <div className="border-b border-blue-800 pb-4">
                  <h3 className="font-heading text-base font-bold text-blue-400 mb-2">Milwaukee</h3>
                  <p className="text-sm text-blue-100 mb-2">
                    Look for a white label with barcode on the tool body. The serial number is below the barcode, typically starting with letters followed by numbers (e.g., "A12B123456").
                  </p>
                  <div className="text-sm text-blue-300 italic">Common locations: Main body near the trigger, bottom of handle, or inside the battery compartment.</div>
                </div>

                {/* Metabo */}
                <div className="border-b border-blue-800 pb-4">
                  <h3 className="font-heading text-base font-bold text-blue-400 mb-2">Metabo</h3>
                  <p className="text-sm text-blue-100 mb-2">
                    Metabo places serial numbers on a rating plate, usually on the motor housing. The number is typically 8-10 digits and may include manufacturing date codes.
                  </p>
                  <div className="text-sm text-blue-300 italic">Common locations: Side of the tool body, near the gear housing, or on the base.</div>
                </div>

                {/* Paslode */}
                <div className="border-b border-blue-800 pb-4">
                  <h3 className="font-heading text-base font-bold text-blue-400 mb-2">Paslode</h3>
                  <p className="text-sm text-blue-100 mb-2">
                    Paslode nail guns have serial numbers engraved or on a label on the main body. Look near the magazine or on the side of the combustion chamber housing.
                  </p>
                  <div className="text-sm text-blue-300 italic">Common locations: Side of the body, near the fuel cell compartment, or on the handle.</div>
                </div>

                {/* Ryobi */}
                <div className="border-b border-blue-800 pb-4">
                  <h3 className="font-heading text-base font-bold text-blue-400 mb-2">Ryobi</h3>
                  <p className="text-sm text-blue-100 mb-2">
                    Ryobi serial numbers are on a green or white label on the tool body. The format varies but is usually 10-14 characters including letters and numbers.
                  </p>
                  <div className="text-sm text-blue-300 italic">Common locations: Bottom of the tool, side of motor housing, or near the battery connection.</div>
                </div>

                {/* Snap-on */}
                <div className="border-b border-blue-800 pb-4">
                  <h3 className="font-heading text-base font-bold text-blue-400 mb-2">Snap-on</h3>
                  <p className="text-sm text-blue-100 mb-2">
                    Snap-on tools typically have serial numbers engraved on the tool itself or on a small metal plate. Air tools often have the number near the air inlet.
                  </p>
                  <div className="text-sm text-blue-300 italic">Common locations: Engraved on the body, on a riveted plate, or near the drive mechanism.</div>
                </div>

                {/* Stihl */}
                <div className="border-b border-blue-800 pb-4">
                  <h3 className="font-heading text-base font-bold text-blue-400 mb-2">Stihl</h3>
                  <p className="text-sm text-blue-100 mb-2">
                    Stihl serial numbers are on a silver plate on the crankcase or motor housing. Chainsaws have it near the rear handle. Format is typically 9 digits.
                  </p>
                  <div className="text-sm text-blue-300 italic">Common locations: Side of the engine/motor, near the starter, or on the crankcase.</div>
                </div>

                {/* Can't find it? */}
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <h3 className="font-heading text-base font-bold text-white mb-2">Can't Find the Serial Number?</h3>
                  <ul className="text-sm text-blue-200 space-y-1">
                    <li>• Check the original box or receipt</li>
                    <li>• Look inside battery compartments</li>
                    <li>• Check under removable covers or plates</li>
                    <li>• Some tools have it engraved rather than on a label</li>
                    <li>• Contact the manufacturer with your model number</li>
                  </ul>
                </div>

              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
